import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import Button from '../components/ui/Button';
import BandeauInstitutionnel from '../components/layout/BandeauInstitutionnel';
import { ACCUEIL, ESPACE_ADMIN } from '../utils/espace';
import { authService } from '../services/api/auth';

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // « Mot de passe oublié ? » n'avait aucun gestionnaire : le clic ne faisait
  // rien, alors que c'est exactement le moment où l'agent est bloqué. Tant que
  // la messagerie n'est pas configurée, aucun envoi automatique n'est possible :
  // on affiche donc la procédure réelle au lieu de promettre un courriel.
  const [aideMotDePasse, setAideMotDePasse] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    
    console.log('🔐 Tentative de connexion avec:', data.email);

    try {
      // Connexion via le service Auth connecté au backend
      console.log('📡 Appel AuthService.login() - API Backend...');
      const { user } = await authService.login(data.email, data.password);
      
      console.log('✅ Connexion réussie:', user);
      console.log('🔑 Tokens JWT reçus du backend');
      console.log('🔄 Redirection vers /workspace...');
      
      // Les tokens et l'utilisateur sont déjà stockés par authService
      navigate(ACCUEIL);
    } catch (err: any) {
      console.error('❌ Erreur de connexion:', err);
      
      // Afficher le message d'erreur du backend
      if (err.message.includes('Identifiants invalides')) {
        setError('Email ou mot de passe incorrect');
      } else if (err.message.includes('Compte utilisateur désactivé')) {
        setError('Votre compte a été désactivé. Contactez l\'administrateur.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        setError(err.message || 'Une erreur est survenue lors de la connexion');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const classeChamp = (enErreur: boolean) =>
    `w-full rounded-xl border bg-eg-blanc py-2.5 pl-10 pr-3 text-[14px] text-eg-encre
     placeholder:text-eg-estompe transition-colors focus:outline-none focus:ring-4
     disabled:cursor-not-allowed disabled:opacity-60 ${
       enErreur
         ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
         : 'border-eg-bord focus:border-eg-accent focus:ring-eg-accent/15'
     }`;

  return (
    <div className="flex min-h-screen flex-col bg-eg-fond">
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-8 text-center">
            <img src="/logo-omnia.svg" alt="OmnIA" className="mx-auto mb-5 h-12 w-auto" />
            <h1 className="text-[26px] font-semibold tracking-tight text-eg-encre">
              {ESPACE_ADMIN ? 'Administration OmnIA' : 'Connexion à OmnIA'}
            </h1>
            <p className="mt-1.5 text-[14px] text-eg-gris">
              {ESPACE_ADMIN
                ? 'Mémoire du PEV, comptes et paramètres de la plateforme'
                : "L'assistant du Programme Élargi de Vaccination"}
            </p>
          </div>

          <div className="rounded-2xl bg-eg-blanc p-7 shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
                  <p className="text-[13px] text-red-700">{error}</p>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-eg-brun">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="votre@email.com"
                    disabled={isLoading}
                    required
                    className={classeChamp(!!errors.email)}
                    {...register('email')}
                  />
                  <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-eg-estompe" />
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-[12.5px] text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-eg-brun">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Votre mot de passe"
                    disabled={isLoading}
                    required
                    className={`${classeChamp(!!errors.password)} pr-10`}
                    {...register('password')}
                  />
                  <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-eg-estompe" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-eg-estompe hover:text-eg-encre"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-[12.5px] text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-[13px]">
                <label className="flex items-center gap-2 text-eg-gris">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-eg-bord text-eg-accent focus:ring-eg-accent/30"
                  />
                  Se souvenir de moi
                </label>
                <button
                  type="button"
                  onClick={() => setAideMotDePasse((ouvert) => !ouvert)}
                  aria-expanded={aideMotDePasse}
                  className="font-medium text-eg-accent hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              {aideMotDePasse && (
                <p className="rounded-xl bg-eg-papier px-3.5 py-2.5 text-[13px] leading-relaxed text-eg-brun">
                  La plateforme n'envoie pas encore de courriel de
                  réinitialisation. Demandez à l'administrateur de votre
                  organisation de vous générer un mot de passe provisoire : il le
                  fait depuis l'espace Mémoire du PEV, et vous le transmet
                  directement.
                </p>
              )}

              <Button
                type="submit"
                className="w-full !rounded-full !py-2.5"
                loading={isLoading}
                disabled={isLoading}
              >
                Se connecter
              </Button>

              {/* La plateforme n'est pas ouverte à l'inscription : les comptes
                  sont créés par un administrateur. On dit la vraie procédure
                  plutôt que d'offrir une porte qui ne s'ouvre pas. */}
              <p className="text-center text-[12.5px] text-eg-estompe">
                Les comptes sont créés par l'administrateur de la plateforme.
              </p>
            </form>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-[13px] text-eg-gris hover:text-eg-encre">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
      <BandeauInstitutionnel />
    </div>
  );
};

export default LoginPage;
